/**
 * ProductForm.jsx — componente unificado de criação e edição de produto
 *
 * Rotas:
 *   /produtos/novo          → modo criação
 *   /produtos/editar/:id    → modo edição
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Save, Copy, Check,
  Plus, Trash2, Edit2, X, ChevronDown, ChevronUp,
  Package, RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import LanguageSelector from '../components/LanguageSelector';
import {
  TRANSLATIONS, AD_TEMPLATES, fillTemplate,
  validateHeadline, validateDescription,
  validateSitelinkText, validateSitelinkDesc,
  validateCallout, validatePriceItem,
  SNIPPET_HEADERS,
} from '../i18n/translations';

const API_BASE = 'https://api.sobevenda.com.br/api-proxy/products';
const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', BRL: 'R$' };
const getCurrencySymbol = (c) => CURRENCY_SYMBOLS[c] || '$';

// ─── Mapa: label amigável → enum do Google Ads ────────────────────────────────
// "Benefits" NÃO existe na API do Google — removido
const SNIPPET_HEADER_ENUM = {
  'Amenities':          'AMENITIES',
  'Brands':             'BRANDS',
  'Courses':            'COURSES',
  'Degree programs':    'DEGREE_PROGRAMS',
  'Destinations':       'DESTINATIONS',
  'Featured hotels':    'FEATURED_HOTELS',
  'Insurance coverage': 'INSURANCE_COVERAGE',
  'Models':             'MODELS',
  'Neighborhoods':      'NEIGHBORHOODS',
  'Service catalog':    'SERVICE_CATALOG',
  'Shows':              'SHOWS',
  'Styles':             'STYLES',
  'Types':              'TYPES',
};
const SNIPPET_HEADER_OPTIONS = Object.keys(SNIPPET_HEADER_ENUM);

// Display path no idioma do anúncio (campo 2)
const DISPLAY_PATH_2 = {
  en: 'Official-Offer',
  pt: 'Oferta-Oficial',
  es: 'Oferta-Oficial',
  fr: 'Offre-Officielle',
  de: 'Offizielles-Angebot',
  it: 'Offerta-Ufficiale',
};

// ─── Ícones de pin (SVG inline — compatível com qualquer versão do lucide) ────
const PinIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="17" x2="12" y2="22"/>
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
  </svg>
);
const PinOffIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" y1="2" x2="22" y2="22"/>
    <line x1="12" y1="17" x2="12" y2="22"/>
    <path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h12"/>
    <path d="M15 9.34V6h1a2 2 0 0 0 0-4H7.89"/>
  </svg>
);

// ─── StepIndicator ────────────────────────────────────────────────────────────

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-3 mt-4">
      {[1, 2].map((n) => (
        <div key={n} className="flex items-center gap-2">
          {n === 2 && <div className={`flex-1 h-px w-12 ${step >= 2 ? 'bg-slate-900 dark:bg-slate-100' : 'bg-slate-200 dark:bg-slate-700'}`} />}
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border ${
            step >= n
              ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white'
              : 'border-slate-300 dark:border-slate-600 text-slate-400'
          }`}>{n}</div>
          <span className={`text-xs font-medium ${step >= n ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400'}`}>
            {n === 1 ? 'Informações' : 'Revisar conteúdo'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── SelectableItem ───────────────────────────────────────────────────────────

function SelectableItem({ text, selected, pinned, onToggle, onEdit, onCopy, onDelete, onPin, copied, multiline = false, showPin = false }) {
  // pinned pode ser undefined/null (não pinado) ou { idx, position } (pinado)
  const isPinned = !!pinned;
  const pinPosition = pinned?.position;

  return (
    <div
      onClick={onToggle}
      className={`flex items-start gap-3 p-3 rounded-lg border transition cursor-pointer ${
        isPinned
          ? 'border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-900/10'
          : selected
            ? 'border-emerald-300 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50'
      }`}
    >
      <div className={`mt-0.5 shrink-0 w-4 h-4 rounded border flex items-center justify-center transition ${
        selected || isPinned
          ? 'bg-emerald-600 border-emerald-600 dark:bg-emerald-500 dark:border-emerald-500'
          : 'border-slate-300 dark:border-slate-600'
      }`}>
        {(selected || isPinned) && <Check className="w-2.5 h-2.5 text-white" />}
      </div>
      <span className={`flex-1 text-sm text-slate-800 dark:text-slate-200 ${multiline ? 'leading-relaxed' : ''}`}>
        {isPinned && (
          <span className="mr-1.5 inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold bg-amber-500 text-white">
            {pinPosition}
          </span>
        )}
        {text}
      </span>
      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
        {showPin && (
          <button
            onClick={onPin}
            title={isPinned ? `Fixado na pos ${pinPosition} — clique para avançar/remover` : 'Fixar nesta posição'}
            className={`p-1 rounded transition ${isPinned ? 'text-amber-500 hover:text-amber-700' : 'text-slate-400 hover:text-amber-500'}`}
          >
            {isPinned ? <PinOffIcon className="w-3.5 h-3.5" /> : <PinIcon className="w-3.5 h-3.5" />}
          </button>
        )}
        <button onClick={onEdit} className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"><Edit2 className="w-3.5 h-3.5" /></button>
        <button onClick={onCopy} className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}</button>
        <button onClick={onDelete} className="p-1 rounded text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

// ─── CollapsibleSection ───────────────────────────────────────────────────────

function CollapsibleSection({ title, count, expanded, onToggle, children, action }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition text-left">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
          {title}
          <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">({count})</span>
        </h3>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {action}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {expanded && <div className="p-4 pt-0 border-t border-slate-100 dark:border-slate-800">{children}</div>}
    </div>
  );
}

// ─── ProductForm ──────────────────────────────────────────────────────────────

export default function ProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const defaultBasic = {
    name: '', niche: '', producer_url: '', affiliate_link: '',
    anchor_price: '', price_per_bottle: '', currency: 'USD',
    bundle_quantity: 6, warranty_days: 60,
    has_bonus: false, bonus_description: '', free_shipping: true,
    language: 'en',
    discount_1_type: 'percentage', discount_1_value: 72,
    discount_2_type: 'monetary', discount_2_value: 780,
  };

  const [basicData, setBasicData] = useState(defaultBasic);
  const [calculatedPrices, setCalculatedPrices] = useState({
    final_price_1: 0, savings_1: 0,
    final_price_2: 0, savings_2: 0,
    bottle_price_full: 0,
  });

  const [headlines, setHeadlines] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  // keywords são objetos { text, match_type }
  const [keywords, setKeywords] = useState([]);
  const [negativeKeywords, setNegativeKeywords] = useState([]);
  const [sitelinks, setSitelinks] = useState([]);
  const [callouts, setCallouts] = useState([]);
  const [priceExtensions, setPriceExtensions] = useState([]);
  const [promotionExtensions, setPromotionExtensions] = useState([]);
  const [structuredSnippet, setStructuredSnippet] = useState(null);

  const [selectedHeadlines, setSelectedHeadlines] = useState([]);
  // pinnedHeadlines: array de { idx, position } onde position = 1, 2 ou 3
  const [pinnedHeadlines, setPinnedHeadlines] = useState([]);
  const [selectedDescriptions, setSelectedDescriptions] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [copiedItems, setCopiedItems] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState({
    keywords: false, negative: false, sitelinks: false,
    callouts: false, price: false, promotion: false, structured: false,
  });

  const [newKeyword, setNewKeyword] = useState('');
  const [newKeywordMatch, setNewKeywordMatch] = useState('PHRASE');
  const [newNegativeKeyword, setNewNegativeKeyword] = useState('');
  const [newCallout, setNewCallout] = useState('');
  const [editingSitelink, setEditingSitelink] = useState(null);
  const [editingPriceExtension, setEditingPriceExtension] = useState(null);
  const [editingPromotionExtension, setEditingPromotionExtension] = useState(null);
  const [editingSnippet, setEditingSnippet] = useState(false);
  const [newSnippetValue, setNewSnippetValue] = useState('');

  // ── Carregar produto no modo edição ────────────────────────────────────────

  useEffect(() => {
    if (isEditing) fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/get-by-id/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('sobevenda_token')}` },
      });
      if (!res.ok) throw new Error('Produto não encontrado');
      const data = await res.json();
      const p = data.product;

      setBasicData({
        name: p.name || '', niche: p.niche || '',
        producer_url: p.producer_url || '', affiliate_link: p.affiliate_link || '',
        anchor_price: p.anchor_price ?? '',
        price_per_bottle: p.price ?? '',
        currency: p.currency || 'USD',
        bundle_quantity: p.bundle_quantity ?? 6,
        warranty_days: p.warranty_days ?? 60,
        has_bonus: p.has_bonus || false,
        bonus_description: p.bonus_description || '',
        free_shipping: p.free_shipping !== undefined ? p.free_shipping : true,
        language: p.language || 'en',
        discount_1_type: p.discount_1_type || 'percentage',
        discount_1_value: p.discount_1_value ?? 72,
        discount_2_type: p.discount_2_type || 'monetary',
        discount_2_value: p.discount_2_value ?? 780,
      });

      setHeadlines(p.headlines || []);
      setDescriptions(p.descriptions || []);

      // keywords: suporta tanto strings legadas quanto objetos { text, match_type }
      const rawKw = p.keywords || [];
      setKeywords(rawKw.map(k =>
        typeof k === 'string' ? { text: k, match_type: 'PHRASE' } : k
      ));

      setNegativeKeywords(p.negative_keywords || []);
      setSitelinks(p.sitelinks || []);
      setCallouts(p.callouts || []);

      // price_extensions: suporta formato wrapper [{type, items:[...]}] ou direto
      const priceExtRaw = p.price_extensions;
      let priceExt = [];
      if (Array.isArray(priceExtRaw) && priceExtRaw.length > 0) {
        const first = priceExtRaw[0];
        priceExt = first?.items && Array.isArray(first.items) ? first.items : priceExtRaw;
      }
      setPriceExtensions(priceExt);
      setPromotionExtensions(p.promotion_extension || []);

      // structured_snippets: suporta array [{header, values}] ou objeto
      const snippetRaw = p.structured_snippets;
      let snippet = null;
      if (Array.isArray(snippetRaw) && snippetRaw.length > 0) snippet = snippetRaw[0];
      else if (snippetRaw && typeof snippetRaw === 'object' && !Array.isArray(snippetRaw)) snippet = snippetRaw;
      if (snippet && !Array.isArray(snippet.values)) snippet = { ...snippet, values: [] };
      // Converter enum de volta para label amigável se necessário
      if (snippet?.header) {
        const reverseEnum = Object.fromEntries(Object.entries(SNIPPET_HEADER_ENUM).map(([k, v]) => [v, k]));
        snippet = { ...snippet, header: reverseEnum[snippet.header] || snippet.header };
      }
      setStructuredSnippet(snippet);

      setSelectedHeadlines(p.headlines ? p.headlines.map((_, i) => i) : []);
      setSelectedDescriptions(p.descriptions ? p.descriptions.map((_, i) => i) : []);
    } catch (err) {
      setError(err.message || 'Erro ao carregar produto');
    } finally {
      setLoading(false);
    }
  };

  // ── Calcular preços ────────────────────────────────────────────────────────

  useEffect(() => {
    const anchor = parseFloat(basicData.anchor_price) || 0;
    const bundle = parseInt(basicData.bundle_quantity) || 6;
    const calc = (type, value) => {
      const disc = type === 'percentage' ? anchor * (value / 100) : parseFloat(value) || 0;
      return { final: Math.max(0, anchor - disc), savings: Math.max(0, disc) };
    };
    const d1 = calc(basicData.discount_1_type, basicData.discount_1_value);
    const d2 = calc(basicData.discount_2_type, basicData.discount_2_value);
    setCalculatedPrices({
      final_price_1: d1.final, savings_1: d1.savings,
      final_price_2: d2.final, savings_2: d2.savings,
      bottle_price_full: anchor / bundle,
    });
  }, [
    basicData.anchor_price, basicData.bundle_quantity,
    basicData.discount_1_type, basicData.discount_1_value,
    basicData.discount_2_type, basicData.discount_2_value,
  ]);

  // ── Handlers básicos ───────────────────────────────────────────────────────

  const handleBasicChange = (field, value) => { setBasicData({ ...basicData, [field]: value }); setError(''); };

  const handleLanguageChange = (lang) => {
    const t = TRANSLATIONS[lang] || TRANSLATIONS.en;
    setBasicData((prev) => ({
      ...prev, language: lang,
      cookie_title: prev.cookie_title || t.cookie_title,
      cookie_text: prev.cookie_text || t.cookie_text,
      cta_label: prev.cta_label || t.cta_label,
      disclosure_text: prev.disclosure_text || t.disclosure_text,
    }));
    toast.success(`Idioma alterado para ${lang.toUpperCase()}`);
  };

  // ── Headlines: toggle, pin múltiplo (pos 1/2/3), sem duplicatas ──────────

  const toggleHeadline = (idx) => {
    const isPinned = pinnedHeadlines.some(p => p.idx === idx);
    if (isPinned) return; // pinado sempre fica selecionado
    if (selectedHeadlines.includes(idx)) {
      setSelectedHeadlines(selectedHeadlines.filter(i => i !== idx));
    } else {
      if (selectedHeadlines.length >= 15) { setError('Máximo 15 títulos'); return; }
      setSelectedHeadlines([...selectedHeadlines, idx]);
    }
  };

  // Cicla: sem pin → pos 1 → pos 2 → pos 3 → sem pin
  // Sem limite de quantidade — qualquer título pode ser pinado
  // Usa updater funcional (prev =>) para evitar closure stale em cliques rápidos
  const pinHeadline = (idx) => {
    setPinnedHeadlines(prev => {
      const existing = prev.find(p => p.idx === idx);
      if (!existing) {
        // Não pinado → fixa na posição 1
        return [...prev, { idx, position: 1 }];
      } else if (existing.position < 3) {
        // Avança para próxima posição (1 → 2 → 3)
        return prev.map(p => p.idx === idx ? { ...p, position: p.position + 1 } : p);
      } else {
        // Estava na pos 3 → remove o pin
        return prev.filter(p => p.idx !== idx);
      }
    });
    // Garantir que o título pinado está selecionado
    setSelectedHeadlines(prev => {
      if (prev.includes(idx) || prev.length >= 15) return prev;
      return [...prev, idx];
    });
  };

  const toggleDescription = (idx) => {
    if (selectedDescriptions.includes(idx)) {
      setSelectedDescriptions(selectedDescriptions.filter(i => i !== idx));
    } else {
      if (selectedDescriptions.length >= 4) { setError('Máximo 4 descrições'); return; }
      setSelectedDescriptions([...selectedDescriptions, idx]);
    }
  };

  const handleCopy = (text, copyId) => {
    navigator.clipboard.writeText(text);
    setCopiedItems(new Set([...copiedItems, copyId]));
    setTimeout(() => setCopiedItems(prev => { const s = new Set(prev); s.delete(copyId); return s; }), 2000);
  };

  const startEdit = (type, index, value) => setEditingItem({ type, index, value });
  const cancelEdit = () => setEditingItem(null);

  const saveEdit = () => {
    if (!editingItem) return;
    const { type, index, value } = editingItem;
    const trimmed = typeof value === 'string' ? value.trim() : value;

    // Validação anti-duplicata para headlines e descriptions
    if (type === 'headline') {
      const isDup = headlines.some((h, i) => i !== index && h.toLowerCase() === trimmed.toLowerCase());
      if (isDup) { toast.error('Título duplicado.'); return; }
      const next = [...headlines]; next[index] = trimmed; setHeadlines(next);
    } else if (type === 'description') {
      const isDup = descriptions.some((d, i) => i !== index && d.toLowerCase() === trimmed.toLowerCase());
      if (isDup) { toast.error('Descrição duplicada.'); return; }
      const next = [...descriptions]; next[index] = trimmed; setDescriptions(next);
    } else if (type === 'keyword') {
      const isDup = keywords.some((k, i) => i !== index && k.text.toLowerCase() === trimmed.toLowerCase());
      if (isDup) { toast.error('Palavra-chave duplicada.'); return; }
      const next = [...keywords]; next[index] = { ...next[index], text: trimmed }; setKeywords(next);
    } else if (type === 'negative') {
      const next = [...negativeKeywords]; next[index] = trimmed; setNegativeKeywords(next);
    } else if (type === 'callout') {
      const next = [...callouts]; next[index] = trimmed; setCallouts(next);
    }
    setEditingItem(null);
  };

  const deleteHeadline = (i) => {
    setHeadlines(headlines.filter((_, idx) => idx !== i));
    setSelectedHeadlines(selectedHeadlines.filter(idx => idx !== i).map(idx => idx > i ? idx - 1 : idx));
    setPinnedHeadlines(
      pinnedHeadlines
        .filter(p => p.idx !== i)
        .map(p => p.idx > i ? { ...p, idx: p.idx - 1 } : p)
    );
  };
  const deleteDescription = (i) => {
    setDescriptions(descriptions.filter((_, idx) => idx !== i));
    setSelectedDescriptions(selectedDescriptions.filter(idx => idx !== i).map(idx => idx > i ? idx - 1 : idx));
  };

  const toggleSection = (s) => setExpandedSections({ ...expandedSections, [s]: !expandedSections[s] });

  // ── Keywords: objetos { text, match_type } ─────────────────────────────────

  const addKeyword = () => {
    const text = newKeyword.trim();
    if (!text) return;
    const isDup = keywords.some(k => k.text.toLowerCase() === text.toLowerCase() && k.match_type === newKeywordMatch);
    if (isDup) { toast.error('Palavra-chave duplicada.'); return; }
    setKeywords([...keywords, { text, match_type: newKeywordMatch }]);
    setNewKeyword('');
  };

  const addNegativeKeyword = () => {
    const text = newNegativeKeyword.trim();
    if (!text) return;
    const isDup = negativeKeywords.some(k => k.toLowerCase() === text.toLowerCase());
    if (isDup) { toast.error('Negativa duplicada.'); return; }
    setNegativeKeywords([...negativeKeywords, text]);
    setNewNegativeKeyword('');
  };

  const addCallout = () => { if (newCallout.trim()) { setCallouts([...callouts, newCallout.trim()]); setNewCallout(''); } };

  const saveSitelink = (idx, field, value) => { const next = [...sitelinks]; next[idx][field] = value; setSitelinks(next); };
  const deleteSitelink = (i) => setSitelinks(sitelinks.filter((_, idx) => idx !== i));
  const addSitelink = () => {
    setSitelinks([...sitelinks, { url: `{page_url}?src=sitelink_custom${sitelinks.length + 1}`, text: '', description1: '', description2: '' }]);
    setEditingSitelink(sitelinks.length);
  };

  const savePriceExtension = (idx, field, value) => {
    const next = [...priceExtensions];
    next[idx][field] = (field === 'price' || field === 'final_price') ? parseFloat(value) || 0 : value;
    setPriceExtensions(next);
  };

  const savePromotionExtension = (idx, field, value) => {
    const next = [...promotionExtensions];
    if (field === 'discount_value') {
      const v = parseFloat(value) || 0;
      if (next[idx].discount_type === 'PERCENT_OFF' || next[idx].discount_type === 'percentage') next[idx].percent_off = v;
      else next[idx].money_amount_off = v;
      next[idx].discount_value = v;
    } else {
      next[idx][field] = value;
    }
    setPromotionExtensions(next);
  };
  const deletePromotionExtension = (i) => setPromotionExtensions(promotionExtensions.filter((_, idx) => idx !== i));
  const addPromotionExtension = () => {
    setPromotionExtensions([...promotionExtensions, {
      promotion_target: '', discount_type: 'PERCENT_OFF', percent_off: 0,
      final_url: `{page_url}?src=promo_custom${promotionExtensions.length + 1}`,
    }]);
    setEditingPromotionExtension(promotionExtensions.length);
  };

  const saveSnippetValue = () => {
    if (newSnippetValue.trim() && structuredSnippet) {
      const vals = Array.isArray(structuredSnippet.values) ? structuredSnippet.values : [];
      if (vals.length >= 10) { toast.error('Máximo 10 valores.'); return; }
      setStructuredSnippet({ ...structuredSnippet, values: [...vals, newSnippetValue.trim()] });
      setNewSnippetValue('');
    }
  };
  const deleteSnippetValue = (i) => {
    if (structuredSnippet) {
      const vals = Array.isArray(structuredSnippet.values) ? structuredSnippet.values : [];
      setStructuredSnippet({ ...structuredSnippet, values: vals.filter((_, idx) => idx !== i) });
    }
  };

  // ── Validação do step 1 ────────────────────────────────────────────────────

  const validateStep1 = (requirePricing = true) => {
    if (!basicData.name.trim()) { setError('Nome do produto é obrigatório'); return false; }
    if (!basicData.producer_url.trim()) { setError('URL do produtor é obrigatória'); return false; }
    if (!basicData.affiliate_link.trim()) { setError('Link de afiliado é obrigatório'); return false; }
    if (!basicData.price_per_bottle || parseFloat(basicData.price_per_bottle) <= 0) { setError('Preço por unidade é obrigatório'); return false; }
    if (requirePricing && (!basicData.anchor_price || parseFloat(basicData.anchor_price) <= 0)) { setError('Preço âncora é obrigatório'); return false; }
    return true;
  };

  // ── Gerar com IA ───────────────────────────────────────────────────────────

  const handleGenerateWithAI = async () => {
    if (!validateStep1(true)) return;
    setLoading(true); setError('');
    try {
      await new Promise(r => setTimeout(r, 1500));
      const lang = basicData.language || 'en';
      const adTemplates = AD_TEMPLATES[lang] || AD_TEMPLATES.en;
      const sym = getCurrencySymbol(basicData.currency);
      const bundle = parseInt(basicData.bundle_quantity) || 6;
      const pricePerBottle = parseFloat(basicData.price_per_bottle);
      const vars = {
        product: basicData.name, currency: sym,
        price: pricePerBottle.toFixed(2),
        amount: Math.floor(calculatedPrices.savings_2),
        amount2: Math.floor(calculatedPrices.savings_2), // desconto 2
        percent: basicData.discount_1_value,
        days: basicData.warranty_days, qty: bundle,
      };

      // buy_discount: usa amount2 quando o desconto 2 for monetário, senão usa amount
      const buyDiscountVars = basicData.discount_2_type === 'monetary'
        ? { ...vars, amount: vars.amount2 }
        : vars;

      // Gerar e deduplicar headlines
      const rawHeadlines = [
        validateHeadline(fillTemplate(adTemplates.official_site, vars)),
        validateHeadline(fillTemplate(adTemplates.buy_price_per_bottle, vars)),
        validateHeadline(fillTemplate(adTemplates.special_offer, vars)),
        validateHeadline(fillTemplate(adTemplates.save_up_to, vars)),
        validateHeadline(fillTemplate(adTemplates.only_price, vars)),
        validateHeadline(fillTemplate(adTemplates.percent_off, vars)),
        validateHeadline(fillTemplate(adTemplates.free_fast_shipping, vars)),
        // special_promo_off usa amount2 para não duplicar save_up_to (que usa amount)
        validateHeadline(fillTemplate(adTemplates.special_promo_off, { ...vars, amount: vars.amount2 })),
        validateHeadline(fillTemplate(adTemplates.buy_direct_factory, vars)),
        validateHeadline(fillTemplate(adTemplates.special_offer_today, vars)),
        validateHeadline(fillTemplate(adTemplates.free_fast_shipping_short, vars)),
        validateHeadline(fillTemplate(adTemplates.limited_time, vars)),
        validateHeadline(fillTemplate(adTemplates.price_free_ship, vars)),
        validateHeadline(fillTemplate(adTemplates.buy_now_today, vars)),
        validateHeadline(fillTemplate(adTemplates.buy_discount, buyDiscountVars)),
      ];
      const seenH = new Set();
      const uniqueHeadlines = rawHeadlines.filter(h => {
        const key = h.trim().toLowerCase();
        if (!key || seenH.has(key)) return false;
        seenH.add(key); return true;
      });
      setHeadlines(uniqueHeadlines);
      setSelectedHeadlines(uniqueHeadlines.map((_, i) => i));
      setPinnedHeadlines([{ idx: 0, position: 1 }]); // Official Site fixado na pos 1 por padrão

      // Gerar e deduplicar descriptions
      const rawDescs = [
        validateDescription(fillTemplate(adTemplates.desc_1, vars)),
        validateDescription(fillTemplate(adTemplates.desc_2, vars)),
        validateDescription(fillTemplate(adTemplates.desc_3, vars)),
        validateDescription(fillTemplate(adTemplates.desc_4, vars)),
        validateDescription(fillTemplate(adTemplates.desc_5, vars)),
      ];
      const seenD = new Set();
      const uniqueDescs = rawDescs.filter(d => {
        const key = d.trim().toLowerCase();
        if (!key || seenD.has(key)) return false;
        seenD.add(key); return true;
      });
      setDescriptions(uniqueDescs);
      setSelectedDescriptions(uniqueDescs.slice(0, 4).map((_, i) => i));

      // 2 keywords: frase e exata — salvas como strings no formato legado
      // "produto" = PHRASE (aspas), [produto] = EXACT (colchetes)
      // Mesmo formato das negativas que já funcionam, e o worker já parseia ambos
      const productText = basicData.name.toLowerCase().trim();
      setKeywords([
        { text: productText, match_type: 'PHRASE' },
        { text: productText, match_type: 'EXACT' },
      ]);

      setNegativeKeywords(adTemplates.negative_keywords || ['free', 'scam', 'fake', 'pirate', 'torrent', 'crack']);

      setSitelinks([
        { url: '{page_url}?src=sitelink_buy_now', text: validateSitelinkText(fillTemplate(adTemplates.sitelink_buy_now, vars)), description1: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_buy_desc1, vars)), description2: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_buy_desc2, vars)) },
        { url: '{page_url}?src=sitelink_guarantee', text: validateSitelinkText(fillTemplate(adTemplates.sitelink_guarantee, vars)), description1: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_guarantee_desc1, vars)), description2: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_guarantee_desc2, vars)) },
        { url: '{page_url}?src=sitelink_reviews', text: validateSitelinkText(fillTemplate(adTemplates.sitelink_reviews, vars)), description1: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_reviews_desc1, vars)), description2: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_reviews_desc2, vars)) },
        { url: '{page_url}?src=sitelink_shipping', text: validateSitelinkText(fillTemplate(adTemplates.sitelink_shipping, vars)), description1: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_shipping_desc1, vars)), description2: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_shipping_desc2, vars)) },
        { url: '{page_url}?src=sitelink_bundles', text: validateSitelinkText(fillTemplate(adTemplates.sitelink_bundles, vars)), description1: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_bundles_desc1, vars)), description2: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_bundles_desc2, vars)) },
        { url: '{page_url}?src=sitelink_faq', text: validateSitelinkText(fillTemplate(adTemplates.sitelink_faq, vars)), description1: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_faq_desc1, vars)), description2: validateSitelinkDesc(fillTemplate(adTemplates.sitelink_faq_desc2, vars)) },
      ]);

      setCallouts([
        validateCallout(fillTemplate(adTemplates.callout_shipping, vars)),
        validateCallout(fillTemplate(adTemplates.callout_guarantee, vars)),
        validateCallout(fillTemplate(adTemplates.callout_secure, vars)),
        validateCallout(fillTemplate(adTemplates.callout_support, vars)),
      ]);

      const bundles = [bundle, Math.floor(bundle / 2), Math.floor(bundle / 3)];
      setPriceExtensions(bundles.map((qty, i) => ({
        header: validatePriceItem(fillTemplate(adTemplates.price_bottles, { ...vars, qty })),
        description: `${qty}-pack deal`,
        price: pricePerBottle * qty,
        currency_code: basicData.currency || 'USD',
        final_url: `{page_url}?src=price_item${i + 1}`,
      })));

      // Promoções: monetário usa savings calculado (não discount_value bruto)
      const promotions = [];
      [
        { type: basicData.discount_1_type, value: basicData.discount_1_value, savings: calculatedPrices.savings_1, src: 'promo_discount1' },
        { type: basicData.discount_2_type, value: basicData.discount_2_value, savings: calculatedPrices.savings_2, src: 'promo_discount2' },
      ].forEach(({ type, value, savings, src }) => {
        const base = {
          promotion_target: basicData.name.substring(0, 20),
          item: `SAVE${value}`,
          final_url: `{page_url}?src=${src}`,
          language_code: lang.split('-')[0],
        };
        if (type === 'percentage') {
          promotions.push({ ...base, discount_type: 'PERCENT_OFF', percent_off: parseInt(value) });
        } else {
          promotions.push({
            ...base,
            discount_type: 'MONEY_AMOUNT_OFF',
            money_amount_off: parseFloat(savings.toFixed(2)),
            currency_code: basicData.currency || 'USD',
          });
        }
      });
      setPromotionExtensions(promotions);

      // Snippet: 'Types' como padrão (válido no Google), sem 'Benefits'
      setStructuredSnippet({
        header: 'Types',
        values: adTemplates.snippet_values || ['Fast Delivery', 'Warranty', '24/7 Support', 'Free Shipping'],
      });

      setStep(2);
    } catch {
      setError('Erro ao gerar conteúdo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewExisting = () => {
    if (!validateStep1(false)) return;
    setError('');
    setStep(2);
  };

  // ── Salvar ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (selectedHeadlines.length < 3) { setError('Selecione pelo menos 3 títulos'); return; }
    if (selectedDescriptions.length < 2) { setError('Selecione pelo menos 2 descrições'); return; }

    setLoading(true); setError('');
    try {
      const lang = (basicData.language || 'en').split('-')[0];

      // Todos os títulos selecionados — pinados primeiro (para referência), mas o array
      // deve conter TODOS os selecionados. O pin é metadata separada.
      const pinnedIdxs = new Set(pinnedHeadlines.map(p => p.idx));
      const pinnedSorted = [...pinnedHeadlines].sort((a, b) => a.position - b.position);
      const unpinnedSelected = selectedHeadlines.filter(i => !pinnedIdxs.has(i));

      // Array final: pinados primeiro (por posição), depois os demais
      const finalHeadlines = [
        ...pinnedSorted.map(p => headlines[p.idx]),
        ...unpinnedSelected.map(i => headlines[i]),
      ];

      // Display path no idioma do anúncio
      const productSlug = basicData.name.trim().replace(/\s+/g, '-').substring(0, 15);
      const displayPath1 = productSlug;
      const displayPath2 = DISPLAY_PATH_2[lang] || DISPLAY_PATH_2.en;

      const payload = {
        name: basicData.name, niche: basicData.niche || '',
        producer_url: basicData.producer_url, affiliate_link: basicData.affiliate_link,
        language: basicData.language,
        price: parseFloat(basicData.price_per_bottle),
        anchor_price: parseFloat(basicData.anchor_price) || 0,
        currency: basicData.currency || 'USD',
        bundle_quantity: parseInt(basicData.bundle_quantity) || 6,
        discount_1_type: basicData.discount_1_type,
        discount_1_value: parseFloat(basicData.discount_1_value) || 0,
        discount_2_type: basicData.discount_2_type,
        discount_2_value: parseFloat(basicData.discount_2_value) || 0,
        warranty_days: parseInt(basicData.warranty_days) || 60,
        has_bonus: basicData.has_bonus || false,
        bonus_description: basicData.bonus_description || '',
        free_shipping: basicData.free_shipping !== undefined ? basicData.free_shipping : true,

        headlines: finalHeadlines,
        // Enviar posições dos pinados para o worker do Google Ads
        pinned_headline_positions: pinnedHeadlines.map(p => ({
          headline: headlines[p.idx],
          position: p.position,
        })),
        descriptions: selectedDescriptions.map(i => descriptions[i]),

        // Keywords como strings no formato legado: "produto" = PHRASE, [produto] = EXACT
        // Mesmo formato das negativas que já funcionam no Xano/worker
        keywords: keywords.map(k => {
          if (k.match_type === 'EXACT') return `[${k.text}]`;
          if (k.match_type === 'PHRASE') return `"${k.text}"`;
          return k.text; // BROAD
        }),
        negative_keywords: negativeKeywords || [],
        sitelinks: sitelinks || [],
        callouts: callouts || [],

        price_extensions: priceExtensions.length >= 3 ? [{
          language_code: lang, type: 'BRANDS',
          items: priceExtensions.map(pe => ({
            header: pe.header || pe.item || '',
            description: pe.description || '',
            price: pe.price || 0,
            currency_code: pe.currency_code || basicData.currency || 'USD',
            final_url: pe.final_url || '',
          })),
        }] : [],

        promotion_extension: promotionExtensions.map(promo => ({
          promotion_target: promo.promotion_target || promo.title || '',
          language_code: lang,
          discount_type: promo.discount_type,
          ...(promo.discount_type === 'PERCENT_OFF' || promo.discount_type === 'percentage'
            ? { percent_off: promo.percent_off || promo.discount_value || 0 }
            : { money_amount_off: promo.money_amount_off || promo.discount_value || 0, currency_code: promo.currency_code || basicData.currency }
          ),
          item: promo.item || '',
          final_url: promo.final_url || '',
        })),

        // Converter label amigável → enum do Google Ads (sem 'Benefits')
        structured_snippets: structuredSnippet?.header ? [{
          header: SNIPPET_HEADER_ENUM[structuredSnippet.header] || 'TYPES',
          values: Array.isArray(structuredSnippet.values) ? structuredSnippet.values : [],
        }] : [],

        display_path_1: displayPath1,
        display_path_2: displayPath2,
      };

      const url = isEditing ? `${API_BASE}/update/${id}` : `${API_BASE}/create-product`;
      const method = isEditing ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('sobevenda_token')}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao salvar produto');
      navigate('/produtos', {
        state: { message: isEditing ? 'Produto atualizado com sucesso!' : 'Produto criado com sucesso!' },
      });
    } catch (err) {
      setError(err.message || 'Erro ao salvar produto. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // ── Classes reutilizáveis ──────────────────────────────────────────────────

  const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400";
  const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300";
  const sym = getCurrencySymbol(basicData.currency);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div>
          <button onClick={() => step === 1 ? navigate(-1) : setStep(1)} className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-4 transition">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Package className="w-6 h-6" />
            {step === 1 ? (isEditing ? 'Editar produto' : 'Adicionar produto') : 'Revisar conteúdo gerado'}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            {step === 1
              ? (isEditing ? 'Atualize as informações do produto ou gere novos anúncios.' : 'Preencha as informações do produto para gerar o conteúdo dos anúncios.')
              : 'Selecione e edite os títulos, descrições e extensões antes de salvar.'}
          </p>
          <StepIndicator step={step} />
        </div>

        {loading && step === 1 && isEditing && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-slate-500 text-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" /> Carregando produto...
          </div>
        )}

        {error && (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm dark:bg-red-900/20 dark:text-red-400 dark:border-red-900">{error}</div>
        )}

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

              {!isEditing && (
                <div className="md:col-span-2">
                  <LanguageSelector selectedLanguage={basicData.language} onLanguageChange={handleLanguageChange} title="Idioma do mercado-alvo" description="Selecione o idioma do país onde vai vender este produto" />
                </div>
              )}

              <div>
                <label className={labelCls}>Nome do produto <span className="text-red-500">*</span></label>
                <input type="text" value={basicData.name} onChange={(e) => handleBasicChange('name', e.target.value)} placeholder="Ex: ProDentim" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Nicho</label>
                <input type="text" value={basicData.niche} onChange={(e) => handleBasicChange('niche', e.target.value)} placeholder="Ex: saúde dental" className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>URL do produtor <span className="text-red-500">*</span></label>
                <input type="url" value={basicData.producer_url} onChange={(e) => handleBasicChange('producer_url', e.target.value)} placeholder="https://produto.com/product/" className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Link de afiliado (ClickBank) <span className="text-red-500">*</span></label>
                <input type="url" value={basicData.affiliate_link} onChange={(e) => handleBasicChange('affiliate_link', e.target.value)} placeholder="https://hop.clickbank.net/?affiliate=YOUR_ID&vendor=PRODUCT" className={inputCls} />
              </div>

              {/* Precificação */}
              <div className="md:col-span-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-4">Precificação</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Quantidade no bundle <span className="text-red-500">*</span></label>
                    <input type="number" min="1" value={basicData.bundle_quantity} onChange={(e) => handleBasicChange('bundle_quantity', parseInt(e.target.value) || 1)} className={inputCls} />
                    <p className="text-xs text-slate-500 mt-1">Unidades no bundle principal</p>
                  </div>
                  <div>
                    <label className={labelCls}>Preço âncora ({basicData.bundle_quantity} unidades) <span className="text-red-500">*</span></label>
                    <input type="number" step="0.01" value={basicData.anchor_price} onChange={(e) => handleBasicChange('anchor_price', e.target.value)} placeholder="1074" className={inputCls} />
                    <p className="text-xs text-slate-500 mt-1">Preço cheio por unidade: {sym}{calculatedPrices.bottle_price_full.toFixed(2)}</p>
                  </div>
                  <div>
                    <label className={labelCls}>Preço por unidade (nos anúncios) <span className="text-red-500">*</span></label>
                    <input type="number" step="0.01" value={basicData.price_per_bottle} onChange={(e) => handleBasicChange('price_per_bottle', e.target.value)} placeholder="49" className={inputCls} />
                    <p className="text-xs text-slate-500 mt-1">{basicData.bundle_quantity} unidades = {sym}{(parseFloat(basicData.price_per_bottle) * parseInt(basicData.bundle_quantity) || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className={labelCls}>Moeda <span className="text-red-500">*</span></label>
                <select value={basicData.currency} onChange={(e) => handleBasicChange('currency', e.target.value)} className={inputCls}>
                  <option value="USD">USD — Dólar Americano</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="GBP">GBP — Libra Esterlina</option>
                  <option value="CAD">CAD — Dólar Canadense</option>
                  <option value="AUD">AUD — Dólar Australiano</option>
                  <option value="BRL">BRL — Real Brasileiro</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Dias de garantia</label>
                <input type="number" value={basicData.warranty_days} onChange={(e) => handleBasicChange('warranty_days', e.target.value)} className={inputCls} />
              </div>

              {/* Desconto 1 */}
              <div className="md:col-span-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">Desconto 1 (promoção primária)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><label className={labelCls}>Tipo</label>
                    <select value={basicData.discount_1_type} onChange={(e) => handleBasicChange('discount_1_type', e.target.value)} className={inputCls}>
                      <option value="percentage">Percentual (%)</option>
                      <option value="monetary">Monetário</option>
                    </select>
                  </div>
                  <div><label className={labelCls}>{basicData.discount_1_type === 'percentage' ? 'Desconto (%)' : `Desconto (${sym})`}</label>
                    <input type="number" value={basicData.discount_1_value} onChange={(e) => handleBasicChange('discount_1_value', parseFloat(e.target.value) || 0)} className={inputCls} />
                  </div>
                  <div><label className={labelCls}>Preço final</label>
                    <div className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm font-semibold text-slate-900 dark:text-slate-100">{sym}{calculatedPrices.final_price_1.toFixed(2)}</div>
                  </div>
                  <div><label className={labelCls}>Economia</label>
                    <div className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm font-semibold text-slate-900 dark:text-slate-100">{sym}{calculatedPrices.savings_1.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              {/* Desconto 2 */}
              <div className="md:col-span-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">Desconto 2 (promoção secundária)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><label className={labelCls}>Tipo</label>
                    <select value={basicData.discount_2_type} onChange={(e) => handleBasicChange('discount_2_type', e.target.value)} className={inputCls}>
                      <option value="percentage">Percentual (%)</option>
                      <option value="monetary">Monetário</option>
                    </select>
                  </div>
                  <div><label className={labelCls}>{basicData.discount_2_type === 'percentage' ? 'Desconto (%)' : `Desconto (${sym})`}</label>
                    <input type="number" value={basicData.discount_2_value} onChange={(e) => handleBasicChange('discount_2_value', parseFloat(e.target.value) || 0)} className={inputCls} />
                  </div>
                  <div><label className={labelCls}>Preço final</label>
                    <div className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm font-semibold text-slate-900 dark:text-slate-100">{sym}{calculatedPrices.final_price_2.toFixed(2)}</div>
                  </div>
                  <div><label className={labelCls}>Economia</label>
                    <div className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm font-semibold text-slate-900 dark:text-slate-100">{sym}{calculatedPrices.savings_2.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={basicData.has_bonus} onChange={(e) => handleBasicChange('has_bonus', e.target.checked)} className="w-4 h-4 rounded" />
                  <span className="text-slate-700 dark:text-slate-300">Tem bônus?</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={basicData.free_shipping} onChange={(e) => handleBasicChange('free_shipping', e.target.checked)} className="w-4 h-4 rounded" />
                  <span className="text-slate-700 dark:text-slate-300">Frete grátis?</span>
                </label>
              </div>

              {basicData.has_bonus && (
                <div className="md:col-span-2">
                  <label className={labelCls}>Descrição do bônus</label>
                  <textarea value={basicData.bonus_description} onChange={(e) => handleBasicChange('bonus_description', e.target.value)} rows={2} placeholder="Ex: 3 bônus exclusivos..." className={`${inputCls} resize-none`} />
                </div>
              )}
            </div>

            {/* Botões step 1 */}
            {isEditing ? (
              <>
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button onClick={handleReviewExisting} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm font-semibold">
                    Revisar conteúdo existente
                  </button>
                  <button onClick={handleGenerateWithAI} disabled={loading} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                    {loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Gerando...</> : <><Sparkles className="w-4 h-4" /> Gerar novamente</>}
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                  <strong className="text-slate-700 dark:text-slate-300">Revisar conteúdo:</strong> mantém títulos e extensões atuais. ·{' '}
                  <strong className="text-slate-700 dark:text-slate-300">Gerar novamente:</strong> cria novos títulos baseados nas mudanças.
                </p>
              </>
            ) : (
              <button onClick={handleGenerateWithAI} disabled={loading} className="w-full mt-6 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                {loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Gerando conteúdo...</> : <><Sparkles className="w-4 h-4" /> Gerar títulos, descrições e extensões</>}
              </button>
            )}
          </div>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <div className="space-y-4">

            {/* Headlines + Descriptions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Headlines */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                    Títulos <span className="ml-1 text-xs font-normal text-slate-500">({headlines.length})</span>
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">{selectedHeadlines.length}/15 selecionados (mín. 3)</span>
                    <button onClick={() => { setHeadlines([...headlines, '']); setEditingItem({ type: 'headline', index: headlines.length, value: '' }); }} className="inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {pinnedHeadlines.length > 0 && (
                  <div className="mb-2 space-y-1">
                    {[...pinnedHeadlines].sort((a, b) => a.position - b.position).map(p => (
                      <p key={p.idx} className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <PinIcon className="w-3 h-3 shrink-0" />
                        <span>Pos {p.position}: <strong>"{headlines[p.idx]?.substring(0, 25)}"</strong></span>
                      </p>
                    ))}
                  </div>
                )}

                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  {headlines.map((h, idx) => (
                    editingItem?.type === 'headline' && editingItem?.index === idx ? (
                      <div key={idx} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                        <input type="text" value={editingItem.value} maxLength={30} autoFocus onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} className={inputCls} />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-400">{editingItem.value.length}/30</span>
                          <div className="flex gap-2">
                            <button onClick={saveEdit} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"><Check className="w-3 h-3" /> Salvar</button>
                            <button onClick={cancelEdit} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /> Cancelar</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <SelectableItem
                        key={idx} text={h}
                        selected={selectedHeadlines.includes(idx)}
                        pinned={pinnedHeadlines.find(p => p.idx === idx)}
                        showPin
                        onToggle={() => toggleHeadline(idx)}
                        onPin={() => pinHeadline(idx)}
                        onEdit={() => startEdit('headline', idx, h)}
                        onCopy={() => handleCopy(h, `h-${idx}`)}
                        onDelete={() => deleteHeadline(idx)}
                        copied={copiedItems.has(`h-${idx}`)}
                      />
                    )
                  ))}
                </div>
              </div>

              {/* Descriptions */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                    Descrições <span className="ml-1 text-xs font-normal text-slate-500">({descriptions.length})</span>
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">{selectedDescriptions.length}/4 selecionados (mín. 2)</span>
                    <button onClick={() => { setDescriptions([...descriptions, '']); setEditingItem({ type: 'description', index: descriptions.length, value: '' }); }} className="inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  {descriptions.map((d, idx) => (
                    editingItem?.type === 'description' && editingItem?.index === idx ? (
                      <div key={idx} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                        <textarea value={editingItem.value} maxLength={90} rows={2} autoFocus onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} className={`${inputCls} resize-none`} />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-400">{editingItem.value.length}/90</span>
                          <div className="flex gap-2">
                            <button onClick={saveEdit} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"><Check className="w-3 h-3" /> Salvar</button>
                            <button onClick={cancelEdit} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /> Cancelar</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <SelectableItem key={idx} text={d} multiline selected={selectedDescriptions.includes(idx)}
                        onToggle={() => toggleDescription(idx)} onEdit={() => startEdit('description', idx, d)}
                        onCopy={() => handleCopy(d, `d-${idx}`)} onDelete={() => deleteDescription(idx)}
                        copied={copiedItems.has(`d-${idx}`)} />
                    )
                  ))}
                </div>
              </div>
            </div>

            {/* Keywords com match_type */}
            <CollapsibleSection title="Palavras-chave" count={keywords.length} expanded={expandedSections.keywords} onToggle={() => toggleSection('keywords')}>
              <div className="flex gap-2 mb-2 mt-3">
                <input type="text" value={newKeyword} onChange={(e) => setNewKeyword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addKeyword()} placeholder="Nome do produto" className={`${inputCls} flex-1`} />
                <select value={newKeywordMatch} onChange={(e) => setNewKeywordMatch(e.target.value)} className="px-2 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 shrink-0">
                  <option value="BROAD">Ampla</option>
                  <option value="PHRASE">Frase</option>
                  <option value="EXACT">Exata</option>
                </select>
                <button onClick={addKeyword} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm shrink-0">
                  <Plus className="w-4 h-4" /> Adicionar
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-3"><strong>Ampla</strong>: sem símbolos · <strong>Frase</strong>: entre aspas no Google · <strong>Exata</strong>: entre colchetes no Google</p>
              <div className="flex flex-wrap gap-2">
                {keywords.map((kw, idx) => {
                  const matchColors = {
                    EXACT: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
                    PHRASE: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300',
                    BROAD: 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
                  };
                  const matchLabel = { EXACT: '[exata]', PHRASE: '"frase"', BROAD: 'ampla' };
                  const kwText = typeof kw === 'string' ? kw : kw.text;
                  const kwMatch = typeof kw === 'string' ? 'PHRASE' : kw.match_type;
                  return (
                    editingItem?.type === 'keyword' && editingItem?.index === idx ? (
                      <div key={idx} className="flex items-center gap-1">
                        <input type="text" value={editingItem.value} autoFocus onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} className="h-8 px-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm" style={{ width: `${Math.max(100, editingItem.value.length * 8 + 20)}px` }} />
                        <button onClick={saveEdit} className="p-1 text-slate-600 dark:text-slate-300"><Check className="w-3.5 h-3.5" /></button>
                        <button onClick={cancelEdit} className="p-1 text-slate-400"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <div key={idx} className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm ${matchColors[kwMatch] || matchColors.BROAD}`}>
                        <span className="text-xs opacity-60 font-mono">{matchLabel[kwMatch]}</span>
                        <span>{kwText}</span>
                        <button onClick={() => startEdit('keyword', idx, kwText)} className="opacity-0 group-hover:opacity-100 transition p-0.5"><Edit2 className="w-3 h-3" /></button>
                        <button onClick={() => setKeywords(keywords.filter((_, i) => i !== idx))} className="opacity-0 group-hover:opacity-100 transition p-0.5 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    )
                  );
                })}
              </div>
            </CollapsibleSection>

            {/* Palavras-chave negativas */}
            <CollapsibleSection title="Palavras-chave negativas" count={negativeKeywords.length} expanded={expandedSections.negative} onToggle={() => toggleSection('negative')}>
              <div className="flex gap-2 mb-3 mt-3">
                <input type="text" value={newNegativeKeyword} onChange={(e) => setNewNegativeKeyword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNegativeKeyword()} placeholder="Ex: grátis, fraude, falso" className={inputCls} />
                <button onClick={addNegativeKeyword} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm shrink-0"><Plus className="w-4 h-4" /> Adicionar</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {negativeKeywords.map((kw, idx) => (
                  editingItem?.type === 'negative' && editingItem?.index === idx ? (
                    <div key={idx} className="flex items-center gap-1">
                      <input type="text" value={editingItem.value} autoFocus onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} className="h-8 px-2 rounded-lg border border-red-200 dark:border-red-900 bg-transparent text-sm" style={{ width: `${Math.max(80, editingItem.value.length * 8 + 20)}px` }} />
                      <button onClick={saveEdit} className="p-1 text-slate-600 dark:text-slate-300"><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={cancelEdit} className="p-1 text-slate-400"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <div key={idx} className="group flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/10 text-sm text-red-600 dark:text-red-400">
                      <span>{kw}</span>
                      <button onClick={() => startEdit('negative', idx, kw)} className="opacity-0 group-hover:opacity-100 transition p-0.5 hover:text-red-800 dark:hover:text-red-200"><Edit2 className="w-3 h-3" /></button>
                      <button onClick={() => setNegativeKeywords(negativeKeywords.filter((_, i) => i !== idx))} className="opacity-0 group-hover:opacity-100 transition p-0.5 hover:text-red-800 dark:hover:text-red-200"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  )
                ))}
              </div>
            </CollapsibleSection>

            {/* Sitelinks */}
            <CollapsibleSection title="Sitelinks" count={sitelinks.length} expanded={expandedSections.sitelinks} onToggle={() => toggleSection('sitelinks')}
              action={<button onClick={addSitelink} className="inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"><Plus className="w-4 h-4" /></button>}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {sitelinks.map((sl, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 relative group">
                    {editingSitelink === idx ? (
                      <div className="space-y-2">
                        <input type="text" value={sl.text} onChange={(e) => saveSitelink(idx, 'text', e.target.value)} placeholder="Texto do link (máx. 25)" maxLength={25} className={inputCls} />
                        <input type="text" value={sl.description1} onChange={(e) => saveSitelink(idx, 'description1', e.target.value)} placeholder="Descrição 1 (máx. 35)" maxLength={35} className={inputCls} />
                        <input type="text" value={sl.description2} onChange={(e) => saveSitelink(idx, 'description2', e.target.value)} placeholder="Descrição 2 (máx. 35)" maxLength={35} className={inputCls} />
                        <button onClick={() => setEditingSitelink(null)} className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs"><Check className="w-3.5 h-3.5" /> Concluído</button>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{sl.text || 'Sem título'}</div>
                        <div className="text-xs text-slate-500 mt-1">{sl.description1}</div>
                        <div className="text-xs text-slate-500">{sl.description2}</div>
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                          <button onClick={() => setEditingSitelink(idx)} className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteSitelink(idx)} className="p-1 rounded text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Callouts */}
            <CollapsibleSection title="Frases de destaque" count={callouts.length} expanded={expandedSections.callouts} onToggle={() => toggleSection('callouts')}>
              <div className="flex gap-2 mb-3 mt-3">
                <input type="text" value={newCallout} onChange={(e) => setNewCallout(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCallout()} placeholder="Adicionar frase (máx. 25)" maxLength={25} className={inputCls} />
                <button onClick={addCallout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm shrink-0"><Plus className="w-4 h-4" /> Adicionar</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {callouts.map((co, idx) => (
                  editingItem?.type === 'callout' && editingItem?.index === idx ? (
                    <div key={idx} className="flex items-center gap-1">
                      <input type="text" value={editingItem.value} maxLength={25} autoFocus onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} className="h-8 px-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm" style={{ width: `${Math.max(120, editingItem.value.length * 8 + 20)}px` }} />
                      <button onClick={saveEdit} className="p-1 text-slate-600 dark:text-slate-300"><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={cancelEdit} className="p-1 text-slate-400"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <div key={idx} className="group flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300">
                      <span>{co}</span>
                      <button onClick={() => startEdit('callout', idx, co)} className="opacity-0 group-hover:opacity-100 transition p-0.5 hover:text-slate-900 dark:hover:text-slate-100"><Edit2 className="w-3 h-3" /></button>
                      <button onClick={() => setCallouts(callouts.filter((_, i) => i !== idx))} className="opacity-0 group-hover:opacity-100 transition p-0.5 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  )
                ))}
              </div>
            </CollapsibleSection>

            {/* Extensões de preço */}
            <CollapsibleSection title="Extensões de preço" count={priceExtensions.length} expanded={expandedSections.price} onToggle={() => toggleSection('price')}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                {priceExtensions.map((pe, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 relative group">
                    {editingPriceExtension === idx ? (
                      <div className="space-y-2">
                        <div><label className={labelCls}>Título</label>
                          <input type="text" value={pe.header || pe.item || ''} onChange={(e) => savePriceExtension(idx, pe.header !== undefined ? 'header' : 'item', e.target.value)} placeholder="Ex: 4 Frascos" maxLength={25} className={inputCls} />
                        </div>
                        <div><label className={labelCls}>Descrição</label>
                          <input type="text" value={pe.description || ''} onChange={(e) => savePriceExtension(idx, 'description', e.target.value)} placeholder="Ex: Melhor custo-benefício" maxLength={25} className={inputCls} />
                        </div>
                        <div><label className={labelCls}>Preço</label>
                          <input type="number" step="0.01" value={pe.price || 0} onChange={(e) => savePriceExtension(idx, 'price', e.target.value)} className={inputCls} />
                        </div>
                        <button onClick={() => setEditingPriceExtension(null)} className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs"><Check className="w-3.5 h-3.5" /> Concluído</button>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{pe.header || pe.item || '—'}</div>
                        <div className="text-xs text-slate-500 mt-1">{pe.description}</div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1">{sym}{(pe.price || 0).toFixed(2)}</div>
                        <button onClick={() => setEditingPriceExtension(idx)} className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"><Edit2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Extensões de promoção */}
            <CollapsibleSection title="Extensões de promoção" count={promotionExtensions.length} expanded={expandedSections.promotion} onToggle={() => toggleSection('promotion')}
              action={<button onClick={addPromotionExtension} className="inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"><Plus className="w-4 h-4" /></button>}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {promotionExtensions.map((promo, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 relative group">
                    {editingPromotionExtension === idx ? (
                      <div className="space-y-2">
                        <input type="text" value={promo.promotion_target || promo.title || ''} onChange={(e) => savePromotionExtension(idx, 'promotion_target', e.target.value)} placeholder="Nome do produto (máx. 20)" maxLength={20} className={inputCls} />
                        <div className="grid grid-cols-2 gap-2">
                          <div><label className={labelCls}>Tipo</label>
                            <select value={promo.discount_type} onChange={(e) => savePromotionExtension(idx, 'discount_type', e.target.value)} className={inputCls}>
                              <option value="PERCENT_OFF">Percentual (%)</option>
                              <option value="MONEY_AMOUNT_OFF">Monetário</option>
                            </select>
                          </div>
                          <div><label className={labelCls}>Valor</label>
                            <input type="number" step={(promo.discount_type === 'PERCENT_OFF' || promo.discount_type === 'percentage') ? '1' : '0.01'} value={(promo.discount_type === 'PERCENT_OFF' || promo.discount_type === 'percentage') ? (promo.percent_off || 0) : (promo.money_amount_off || 0)} onChange={(e) => savePromotionExtension(idx, 'discount_value', e.target.value)} className={inputCls} />
                          </div>
                        </div>
                        <button onClick={() => setEditingPromotionExtension(null)} className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs"><Check className="w-3.5 h-3.5" /> Concluído</button>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{promo.promotion_target || promo.title || 'Sem título'}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {(promo.discount_type === 'PERCENT_OFF' || promo.discount_type === 'percentage')
                            ? `${promo.percent_off || promo.discount_value || 0}% de desconto`
                            : `${sym}${(promo.money_amount_off || promo.discount_value || 0).toFixed(2)} de desconto`}
                        </div>
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                          <button onClick={() => setEditingPromotionExtension(idx)} className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deletePromotionExtension(idx)} className="p-1 rounded text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Snippet estruturado — apenas opções válidas do Google */}
            {structuredSnippet && (
              <CollapsibleSection title="Snippet estruturado" count={structuredSnippet.values?.length || 0} expanded={expandedSections.structured} onToggle={() => toggleSection('structured')}>
                <div className="mt-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                  <div className="flex items-center justify-between mb-3">
                    {editingSnippet ? (
                      <div className="flex items-center gap-2 flex-1">
                        <select value={structuredSnippet.header} onChange={(e) => setStructuredSnippet({ ...structuredSnippet, header: e.target.value })} className={inputCls}>
                          {SNIPPET_HEADER_OPTIONS.map(h => (
                            <option key={h} value={h}>{h} → {SNIPPET_HEADER_ENUM[h]}</option>
                          ))}
                        </select>
                        <button onClick={() => setEditingSnippet(false)} className="p-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"><Check className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <>
                        <div>
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{structuredSnippet.header}</span>
                          <span className="ml-2 text-xs font-mono text-slate-400">→ {SNIPPET_HEADER_ENUM[structuredSnippet.header] || structuredSnippet.header}</span>
                        </div>
                        <button onClick={() => setEditingSnippet(true)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"><Edit2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 mb-3">
                    <input type="text" value={newSnippetValue} onChange={(e) => setNewSnippetValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveSnippetValue()} placeholder="Adicionar valor" maxLength={25} className={inputCls} />
                    <button onClick={saveSnippetValue} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm shrink-0"><Plus className="w-4 h-4" /> Adicionar</button>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{structuredSnippet.values?.length || 0}/10 valores (mín. 3)</p>
                  <div className="flex flex-wrap gap-2">
                    {(structuredSnippet.values || []).map((val, idx) => (
                      <div key={idx} className="group flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-slate-700 dark:text-slate-300">
                        <span>{val}</span>
                        <button onClick={() => deleteSnippetValue(idx)} className="opacity-0 group-hover:opacity-100 transition p-0.5 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </CollapsibleSection>
            )}

            {/* Botões */}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setStep(1)} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm">
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>
              <button onClick={handleSave} disabled={loading} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                {loading
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /> Salvando...</>
                  : <><Save className="w-4 h-4" /> {isEditing ? 'Salvar alterações' : 'Salvar produto'}</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}